import { useMemo } from 'react';
import type { EmployeePeer } from '@staff-app/shared-types';
import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import { formatDateMedium, formatDateTimeWithSeconds } from '@/lib/format-date';

interface Props {
  peers: EmployeePeer[];
}

// Liveness derived from the WireGuard handshake (mdm-devices.lastHandshakeAt,
// synced by wg-orchestrator every ~2 min from the agent's 25s keepalive).
// online: handshook in the last 3 min · stale: 3–15 min · offline: older / never.
const ONLINE_MS = 3 * 60 * 1000;
const STALE_MS = 15 * 60 * 1000;

type HeartbeatStatus = 'online' | 'stale' | 'offline';

function heartbeatStatus(lastHandshakeAt?: string): HeartbeatStatus {
  if (!lastHandshakeAt) return 'offline';
  const age = Date.now() - new Date(lastHandshakeAt).getTime();
  if (age < ONLINE_MS) return 'online';
  if (age < STALE_MS) return 'stale';
  return 'offline';
}

function formatAgo(lastHandshakeAt?: string): string {
  if (!lastHandshakeAt) return 'never';
  const age = Date.now() - new Date(lastHandshakeAt).getTime();
  if (age < 0) return 'just now';
  const s = Math.floor(age / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_LABEL: Record<HeartbeatStatus, string> = {
  online: 'Online',
  stale: 'Stale',
  offline: 'Offline',
};

function HeartbeatCell({ lastHandshakeAt }: { lastHandshakeAt?: string }) {
  const status = heartbeatStatus(lastHandshakeAt);
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-2">
            <span
              aria-label={STATUS_LABEL[status]}
              className={cn(
                'inline-block h-2 w-2 rounded-full',
                status === 'online' && 'bg-success',
                status === 'stale' && 'bg-warning',
                status === 'offline' && 'bg-muted-foreground',
              )}
            />
            <span className="text-xs text-muted-foreground">{formatAgo(lastHandshakeAt)}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {STATUS_LABEL[status]}
          {lastHandshakeAt ? ` · ${formatDateTimeWithSeconds(lastHandshakeAt, '—')}` : ''}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Groups peers by subnet and renders a table per subnet. Per spec §1.8,
 * grouping is client-side — backend returns a flat list.
 */
export function PeersTableBySubnet({ peers }: Props) {
  const groups = useMemo(() => {
    const map = new Map<string, EmployeePeer[]>();
    for (const peer of peers) {
      const arr = map.get(peer.subnet) ?? [];
      arr.push(peer);
      map.set(peer.subnet, arr);
    }
    // Sort subnets alphabetically for stable display order.
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [peers]);

  if (!groups.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {'No employee peers.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="peers-grouped-by-subnet">
      {groups.map(([subnet, rows]) => (
        <section
          key={subnet}
          className="rounded-lg border bg-card"
          data-testid={`subnet-group-${subnet}`}
        >
          <header className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">{subnet}</h2>
            <span className="text-xs text-muted-foreground">
              {rows.length} {'peers'}
            </span>
          </header>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{'Name'}</TableHead>
                <TableHead>{'Owner email'}</TableHead>
                <TableHead>{'VPN IP'}</TableHead>
                <TableHead>{'Region'}</TableHead>
                <TableHead>{'Last heartbeat'}</TableHead>
                <TableHead>{'Created by role'}</TableHead>
                <TableHead>{'Created'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((peer) => (
                <TableRow key={peer._id} className="hover:bg-accent/50 transition-colors">
                  <TableCell className="font-medium">{peer.name}</TableCell>
                  <TableCell className="text-muted-foreground">{peer.ownerEmail ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{peer.vpnIp}</TableCell>
                  <TableCell>{peer.region}</TableCell>
                  <TableCell>
                    <HeartbeatCell lastHandshakeAt={peer.lastHandshakeAt} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">{peer.createdByRole}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateMedium(peer.createdAt, '—')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ))}
    </div>
  );
}
